import React, { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useHistory, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCheck,
  faSpinner,
  faTicketAlt,
} from "@fortawesome/free-solid-svg-icons";
import { Icon } from "@fortawesome/fontawesome-svg-core";
import { CouponCommander } from "../../store/commanders/coupon.commander";
import { AccountCommander } from "../../store/commanders/account.commander";
import CourseComander from "../../store/commanders/course.comander";
import { createCheckoutSession } from "../../services/purchase/purchase.service";
import PLButton from "../../components/common/button";
import "./payment.scss";
import { Trans, useTranslation } from "react-i18next";

type ProductType = "course" | "background" | "bundle";

const COURSE_PRICE = 25;
const COURSE_PRICE_GA = 15;
const BACKGROUND_PRICE = 35;
const BUNDLE_PRICE_AL = 25;
const BUNDLE_PRICE_GA = 40;
const BUNDLE_PRICE = 50;

const buildBackgroundRedirect = (orderId?: string | null) => {
  return orderId ? `/background?order_id=${encodeURIComponent(orderId)}` : "/background";
};

const generateOrderId = () => {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID();
  }

  return `bg-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
};

const normalizeCouponKey = (value?: string | null) =>
  (value || "").trim().toUpperCase();

const PaymentPage = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const location = useLocation();
  const user = useSelector((state: any) => state.application.user);
  const userData = useSelector((state: any) => state.account.userData);
  const initialProduct = useMemo(() => {
    const params = new URLSearchParams(location.search);
    const product = (params.get("product") || "course").toLowerCase();
    return ["course", "background", "bundle"].includes(product)
      ? (product as ProductType)
      : "course";
  }, [location.search]);
  const [selectedProduct, setSelectedProduct] = useState<ProductType>(initialProduct);
  const [couponMeta, setCouponMeta] = useState<any>(null);
  const [processingCoupon, setProcessingCoupon] = useState(false);
  const [startingCheckout, setStartingCheckout] = useState(false);

  const { register, handleSubmit, setValue, watch } = useForm({
    defaultValues: {
      coupon_key: "",
    },
  });

  const couponValue = watch("coupon_key");
  const backgroundEmail = userData.account_email || userData.alternate_email || user.email;

  const price = useMemo(() => {
    if (selectedProduct === "background") return BACKGROUND_PRICE;
    if (selectedProduct === "bundle") {
      if (userData.state === "AL") return BUNDLE_PRICE_AL;
      if (userData.state === "GA") return BUNDLE_PRICE_GA;
      return BUNDLE_PRICE;
    }
    return userData.state === "GA" ? COURSE_PRICE_GA : COURSE_PRICE;
  }, [selectedProduct, userData.state]);

  const discountedPrice = useMemo(() => {
    if (couponMeta?.discount && couponMeta.discount > 0 && couponMeta.discount < 100) {
      return Number((price * ((100 - couponMeta.discount) / 100)).toFixed(2));
    }

    return price;
  }, [couponMeta, price]);

  const getProductCopy = (product: ProductType) => ({
    title: t(`payment.products.${product}.title`),
    description: t(`payment.products.${product}.description`),
    cta: t(`payment.products.${product}.cta`),
  });
  const productLabel = getProductCopy(selectedProduct);

  const getRedeemSummary = (course?: boolean, backgroundcheck?: boolean) => {
    if (course && backgroundcheck) return t("payment.redeemSummary.bundle");
    if (backgroundcheck) return t("payment.redeemSummary.background");
    return t("payment.redeemSummary.course");
  };

  useEffect(() => {
    setCouponMeta(null);
  }, [selectedProduct]);

  useEffect(() => {
    setSelectedProduct(initialProduct);
  }, [initialProduct]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const checkout = params.get("checkout");
    const productType = (params.get("product_type") || "course") as ProductType;
    const orderId = params.get("order_id");

    if (!checkout) {
      return;
    }

    AccountCommander.cmd_get_account();
    CourseComander.cmd_get_course();

    if (checkout === "success") {
      toast.success(
        productType === "bundle"
          ? t("payment.success.bundle")
          : productType === "background"
            ? t("payment.success.background")
            : t("payment.success.course"),
      );

      if (productType === "background" || productType === "bundle") {
        history.replace(buildBackgroundRedirect(orderId));
        return;
      }

      history.replace("/course");
      return;
    }

    if (checkout === "cancel") {
      toast.info(t("payment.checkoutCancelled"));
      history.replace("/payment");
    }
  }, [history, location.search, t]);

  useEffect(() => {
    if (user.id_coupon && user.id_coupon !== "undefined") {
      setValue("coupon_key", user.id_coupon);
    }
  }, [setValue, user.id_coupon]);

  useEffect(() => {
    if (
      couponMeta?.coupon_key &&
      normalizeCouponKey(couponMeta.coupon_key) !== normalizeCouponKey(couponValue)
    ) {
      setCouponMeta(null);
    }
  }, [couponMeta?.coupon_key, couponValue]);

  const handleRedeemCoupon = async () => {
    if (!couponValue) {
      toast.info(t("payment.couponRequired"));
      return;
    }

    setProcessingCoupon(true);

    try {
      let activeCouponMeta = couponMeta;

      if (
        !activeCouponMeta ||
        normalizeCouponKey(activeCouponMeta.coupon_key) !== normalizeCouponKey(couponValue)
      ) {
        const result: any = await CouponCommander.cmd_validate_coupon(
          couponValue,
          selectedProduct,
        );

        if (!result || result.error) {
          return;
        }

        activeCouponMeta = {
          coupon_key: couponValue,
          discount: 100,
          organization_name: result.organization_name,
          course: result.course,
          backgroundcheck: result.backgroundcheck,
          redeemed_product_type: result.redeemed_product_type,
        };

        setCouponMeta(activeCouponMeta);
      }

      if (!activeCouponMeta || activeCouponMeta.discount !== 100) {
        toast.info(t("payment.fullCouponRequired"));
        return;
      }

      if (
        activeCouponMeta?.backgroundcheck &&
        !backgroundEmail
      ) {
        toast.error(t("payment.emailRequired"));
        return;
      }

      const orderId =
        activeCouponMeta?.backgroundcheck
          ? generateOrderId()
          : undefined;

      const response: any = await CouponCommander.cmd_redeem_coupon({
        coupon_key: couponValue,
        id_sales: user.id_sales,
        id_organization: user.id_organization,
        product_type: selectedProduct,
        order_id: orderId,
        email: backgroundEmail,
        first_name: userData.first_name,
        middle_name: userData.middle_name,
        last_name: userData.last_name,
      });

      if (!response || response.error) {
        return;
      }

      toast.success(t("payment.success.coupon"));
      const params = new URLSearchParams({
        organization:
          response.organization_name || activeCouponMeta.organization_name || "",
        course: response.course ? "true" : "false",
        backgroundcheck: response.backgroundcheck ? "true" : "false",
        redeemed_product_type:
          response.redeemed_product_type ||
          activeCouponMeta.redeemed_product_type ||
          "course",
      });

      if (response.order_id || orderId) {
        params.set("order_id", response.order_id || orderId);
      }

      history.push(`/coupon-redeemed?${params.toString()}`);
    } finally {
      setProcessingCoupon(false);
    }
  };

  const handleCheckout = async () => {
    if ((selectedProduct === "background" || selectedProduct === "bundle") && !backgroundEmail) {
      toast.error(t("payment.emailRequired"));
      return;
    }

    try {
      setStartingCheckout(true);
      const orderId =
        selectedProduct === "background" || selectedProduct === "bundle"
          ? generateOrderId()
          : undefined;

      const paymentSession = await createCheckoutSession({
        product_type: selectedProduct,
        coupon_key: undefined,
        discount: 0,
        success_url: `${window.location.origin}/payment`,
        cancel_url: `${window.location.origin}/payment`,
        order_id: orderId,
        email: backgroundEmail,
        first_name: userData.first_name,
        middle_name: userData.middle_name,
        last_name: userData.last_name,
      });

      window.location.href = paymentSession.checkoutUrl;
    } catch (_error) {
      toast.error(t("payment.checkoutError"));
    } finally {
      setStartingCheckout(false);
    }
  };

  return (
    <div className="payment-container">
      <div className="payment-shell">
        <div className="payment-main">
          <div className="payment-header">
            <span className="payment-header__eyebrow">{t("payment.eyebrow")}</span>
            <h1>{t("payment.title")}</h1>
            <p>{t("payment.description")}</p>
          </div>

          <div className="payment-product-grid">
            {(["course", "background", "bundle"] as ProductType[]).map((product) => (
              <button
                key={product}
                type="button"
                className={`payment-product-card ${
                  selectedProduct === product ? "payment-product-card--active" : ""
                }`}
                onClick={() => setSelectedProduct(product)}
              >
                <span className="payment-product-card__title">
                  {getProductCopy(product).title}
                </span>
                <strong className="payment-product-card__price">
                  ${product === "course"
                    ? userData.state === "GA"
                      ? COURSE_PRICE_GA
                      : COURSE_PRICE
                    : product === "background"
                      ? BACKGROUND_PRICE
                      : userData.state === "AL"
                        ? BUNDLE_PRICE_AL
                        : userData.state === "GA"
                          ? BUNDLE_PRICE_GA
                          : BUNDLE_PRICE}
                </strong>
                <span className="payment-product-card__description">
                  {getProductCopy(product).description}
                </span>
              </button>
            ))}
          </div>

        </div>

        <div className="payment-summary-card">
          <div className="payment-summary-card__top">
            <div>
              <span className="payment-summary-card__eyebrow">{t("payment.selected")}</span>
              <h2>{productLabel.title}</h2>
              <p>{productLabel.description}</p>
            </div>
            <div className="payment-summary-card__price">
              <span>{t("payment.total")}</span>
              <strong>${discountedPrice.toFixed(2)}</strong>
            </div>
          </div>

          <form
            onSubmit={handleSubmit(handleRedeemCoupon)}
            className="payment-coupon-form payment-coupon-form--inline"
          >
            <div className="payment-coupon-form__copy payment-coupon-form__copy--inline">
              <span className="payment-coupon-form__eyebrow">{t("payment.coupon")}</span>
              <h2>{t("payment.couponTitle")}</h2>
              <p>{t("payment.couponDescription")}</p>
            </div>

            <div className="payment-coupon-form__row payment-coupon-form__row--inline">
              <div className="payment-coupon-form__field payment-coupon-form__field--inline">
                <label htmlFor="coupon_key">{t("payment.couponCode")}</label>
                <input
                  id="coupon_key"
                  className="coupon-input"
                  placeholder={t("payment.couponPlaceholder")}
                  {...register("coupon_key")}
                />
              </div>
            </div>

            {processingCoupon ? (
              <div className="coupon-text">
                <FontAwesomeIcon icon={faSpinner as Icon} className="fa-pulse" />
                <span>{t("payment.checkingCouponLong")}</span>
              </div>
            ) : null}

            {couponMeta?.coupon_key ? (
              <div className="coupon-text coupon-text--success">
                <FontAwesomeIcon
                  icon={faCheck as Icon}
                  className="coupon-text-icon"
                />
                <span>
                  <Trans
                    i18nKey="payment.couponProvidedBy"
                    values={{
                      organization: couponMeta.organization_name,
                      access: getRedeemSummary(couponMeta.course, couponMeta.backgroundcheck),
                    }}
                    components={{ organization: <strong />, access: <strong /> }}
                  />
                </span>
              </div>
            ) : null}

            <div className="payment-actions">
              <PLButton
                type="submit"
                variant="primary"
                size="lg"
                className="payment-secondary-btn"
                disabled={processingCoupon || !couponValue}
              >
                <FontAwesomeIcon icon={faTicketAlt as Icon} />
                <span>{processingCoupon ? t("payment.checkingCoupon") : t("payment.redeemCoupon")}</span>
              </PLButton>

              <PLButton
                type="button"
                variant={selectedProduct === "course" ? "secondary" : "blue"}
                size="lg"
                className="payment-primary-btn"
                onClick={handleCheckout}
                disabled={startingCheckout}
              >
                <span className="payment-primary-btn__label">
                  {startingCheckout ? t("common.redirecting") : productLabel.cta}
                </span>
                <span className="payment-primary-btn__price">
                  ${price.toFixed(2)}
                </span>
              </PLButton>
            </div>
          </form>

          {(selectedProduct === "background" || selectedProduct === "bundle") && !backgroundEmail ? (
            <p className="payment-note">
              {t("payment.emailNote")}
            </p>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default PaymentPage;
